import { IBaseBlogRollWidgetStyle, IFontData } from "@pageBuilder/typings";
import { CssProperties } from "../../../models/css-property";
import { WidgetAnimationRenderer } from "../widget-animation.renderer";
import { ElementStyleService } from "../../../services/element-style.service";
import { StringUtil } from "../../../utils/string-util";

export class BlogRollBaseStylesRenderer {

    protected readonly animationRenderer: WidgetAnimationRenderer;

    constructor(protected readonly styleService: ElementStyleService) {
        this.animationRenderer = new WidgetAnimationRenderer(styleService);
    }

    public render(element: HTMLElement, style: IBaseBlogRollWidgetStyle): void {
        if (!style) {
            this.cleanup(element);
            return;
        }

        const widgetProps = new CssProperties();
        this.applyFont(widgetProps, style.commonFont);
        this.styleService.setStyle(element, `#${element.id} .widget-content`, widgetProps.data);

        const hoverProps = new CssProperties();
        hoverProps.tryAdd("color", style.hoverTextColor);
        this.styleService.setStyle(
            element,
            `#${element.id} .post:hover .post-description, #${element.id} .post:hover .post-title`,
            hoverProps.data
        );

        const titleProps = new CssProperties();
        titleProps.tryAdd("color", style.titleColor);
        titleProps.tryAdd("--blog-roll-title-color", style.titleColor);
        this.applyFont(titleProps, style.titleFont);
        titleProps.tryAdd("--title-lines-count", style.linesForTitle.toString());
        this.styleService.setStyle(element, `#${element.id} .post-title`, titleProps.data);

        const postProps = new CssProperties();
        postProps.tryAdd("border-color", style.borderColor);
        postProps.tryAdd("border-width", style.borderWidth);
        postProps.tryAdd("border-style", "solid");
        postProps.tryAdd("background-color", style.postBackground);
        this.styleService.setStyle(element, `#${element.id} .post`, postProps.data);

        const authorProps = new CssProperties();
        authorProps.tryAdd("color", style.authorColor);
        this.styleService.setStyle(element, `#${element.id} .author-name`, authorProps.data);

        const publishDateProps = new CssProperties();
        publishDateProps.tryAdd("color", style.publishDateColor);
        this.styleService.setStyle(element, `#${element.id} .date`, publishDateProps.data);

        this.animationRenderer.setAnimationStyles(element, style.animation);
    }

    protected applyFont(props: CssProperties, font?: IFontData): void {
        if (font?.size) {
            props.tryAdd("--font-size", String(StringUtil.getNumberFromStr(font.size)));
        }
        props.tryAdd("font-weight", font?.weight);
        props.tryAdd("font-family", font?.family);
        props.tryAdd("line-height", font?.lineHeight);
        props.tryAdd("letter-spacing", font?.letterSpacing);
        props.tryAdd("text-indent", font?.textIndent);
        props.tryAdd("padding-inline-start", font?.paddingInlineStart);
    }

    public cleanup(element: HTMLElement): void {
        const selectors = this.getStyleSelectors(element);
        selectors.forEach(selector => {
            this.styleService.clearStyle(element, selector);
        });
    }

    public getStyleSelectors(element: HTMLElement): string[] {
        return [
            `#${element.id}`,
            `#${element.id} .widget-content`,
            `#${element.id} .post`,
            `#${element.id} .post-title`,
            `#${element.id} .author-name`,
            `#${element.id} .date`,
            `#${element.id} .post:hover .post-description, #${element.id} .post:hover .post-title`
        ];
    }

}
